<script lang="ts" setup>
import { Pulse } from '@vue-interface/activity-indicator';
import { SelectField } from '@vue-interface/select-field';
import type { Folder, Instance } from 'types';
import { onMounted, ref, toRef, watch } from 'vue';
import axios from '../axios';

const emit = defineEmits<{
    (e: 'update:modelValue', value: Folder | undefined): void,
    (e: 'activity', value: boolean): void,
    (e: 'change', value: string | number): void,
}>();

const props = defineProps<{
    id?: string | number | undefined,
    name: string,
    instance: Instance|undefined,
    errors?: string[] | Record<string, string[]>,
    error?: string,
    modelValue: Folder | object | undefined,
}>();

const activity = ref(false);
const folders = ref<Folder[]>([]); 
const options = ref<{ label: string; value: number }[]>([]);
const modelId = ref<string | number | undefined>(props.modelValue && 'id' in props.modelValue ? props.modelValue.id as number : undefined);
const instance = toRef(props, 'instance');

function extractFolders(data: Folder[]): Folder[] {
    return (data || []).reduce((carry: Folder[], folder: Folder) => {
        return carry.concat([folder, ...extractFolders(folder.children)]);
    }, []);
}

function extractOptions(data: Folder[], depth:number = 0): { label: string; value: number }[] {
    return (data || []).reduce<{ label: string; value: number }[]>((carry, folder) => {
        return carry.concat([{
            label: `${Array(depth + 1).join('--')} ${folder.name}`.trim(),
            value: folder.id,
        }, ...extractOptions(folder.children, depth + 1)]);
    }, []);
}

async function fetch(): Promise<void> {
    if(!props.instance) {
        return;
    }
    activity.value = true;
    return await axios.get('mg/folders', {
        params: {
            instance_id: props.instance.id
        }
    }).then(response => {
        folders.value = extractFolders(response.data);
        options.value = extractOptions(response.data);
    }).finally(() => {
        activity.value = false;
    });
}

watch(modelId, (value: string | number | undefined) => {
    const match = folders.value.find(({ id }: { id: number }) => {
        return id === parseInt(String(value));
    });
    emit('update:modelValue', match);
});

watch(instance, () => {
    modelId.value = undefined;
    fetch();
});

watch(activity, (value: boolean) => {
    emit('activity', value);
});

onMounted(async() => {
    await fetch();
});

</script>

<template>
    <SelectField
        :id="id?.toString()"
        v-model="modelId"
        :name="name"
        :disabled="activity"
        :activity="activity"
        :indicator="Pulse"
        :errors="errors"
        :error="error"
        indicator-size="activity-indicator-xs"
        @change="($event: Event) => $emit('change', ($event.target as HTMLSelectElement).value)">
        <option
            v-for="folder in options"
            :key="folder.value"
            :value="folder.value">
            {{ folder.label }}
        </option>
    </SelectField>
</template>